/**
 * Pure H OS · Sprint 12/13/14 · Session + company + workspace state.
 *
 * Real JWT authentication against `/api/pureh/auth/*` — HttpOnly cookies
 * carry the token; `localStorage` only caches the last-known user for
 * reload smoothness.
 *
 * After a valid session is established the provider fetches every
 * mocked collection in one shot (`GET /api/pureh/data`) and hydrates the
 * live store so `PRODUCTS`, `ORDERS`, `EXECUTIVE_DASHBOARD_DATA`, … all
 * resolve to MongoDB rows. `dataReady` gates the app shell so pages
 * only render once fresh data is available.
 */
import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from "react";
import { COMPANIES, WORKSPACES, Company, Workspace, SessionUser } from "../mocks/data";
import { api, ApiError, PurehUser, Market, setActiveMarketHeader } from "../services/api";
import { hydrateAll } from "../services/store";

const USER_CACHE_KEY = "pureh:user:v2";
const MARKET_KEY = "pureh:market:v1";

type PureHState = {
  user: SessionUser | null;
  companyId: string | null;
  workspaceId: string | null;
  markets: Market[];
  marketId: string | null;
  authReady: boolean;   // true after we've asked the server about the current cookie
  dataReady: boolean;   // true after the bootstrap payload has been hydrated
};

type PureHContextValue = Omit<PureHState, "authReady"> & {
  ready: boolean;      // authReady && (user ? dataReady : true) — kept for API compat
  authReady: boolean;
  companies: Company[];
  workspaces: Workspace[];
  currentCompany: Company | null;
  currentWorkspace: Workspace | null;
  currentMarket: Market | null;
  activeCurrency: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setCompany: (id: string) => void;
  setWorkspace: (id: string) => void;
  setMarket: (id: string) => void;
};

const PureHContext = createContext<PureHContextValue | undefined>(undefined);

function fromServer(u: PurehUser): SessionUser {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    role: u.role === "owner" ? "Owner" : u.role === "admin" ? "Admin" : u.role === "manager" ? "Manager" : "Employee",
    avatar: (u.name || u.email).slice(0, 2).toUpperCase(),
    title: u.role === "owner" ? "Founder · Owner"
         : u.role === "admin" ? "Administrator"
         : u.role === "manager" ? "Manager"
         : "Employee",
    isHoldingOwner: !!(u as any).isHoldingOwner || !!(u as any).groupOwner,
  } as SessionUser;
}

function readCached(): SessionUser | null {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch { return null; }
}

export function PureHProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PureHState>(() => ({
    user: readCached(),
    companyId: null,
    workspaceId: null,
    markets: [],
    marketId: null,
    authReady: false,
    dataReady: false,
  }));

  // Market scope must be resolved BEFORE the data bootstrap so the
  // X-Market-Id header rides on /data (fail-closed on the backend).
  const loadMarkets = useCallback(async () => {
    try {
      const all = await api.markets();
      const active = all.filter((m) => m.status === "active");
      let mid: string | null = null;
      try { mid = localStorage.getItem(MARKET_KEY); } catch { /* noop */ }
      if (!mid || !active.some((m) => m.id === mid)) mid = active[0]?.id ?? null;
      setActiveMarketHeader(mid);
      setState((s) => ({ ...s, markets: active, marketId: mid }));
    } catch {
      setActiveMarketHeader(null);
      setState((s) => ({ ...s, markets: [], marketId: null }));
    }
  }, []);

  const bootstrapData = useCallback(async () => {
    const payload = await api.bootstrap();
    hydrateAll(payload);
    // Default company / workspace from the freshly loaded collections.
    const c0 = COMPANIES[0] ?? null;
    const w0 = WORKSPACES.find((w) => !c0 || w.companyId === c0.id) ?? WORKSPACES[0] ?? null;
    setState((s) => ({
      ...s,
      companyId: s.companyId ?? c0?.id ?? null,
      workspaceId: s.workspaceId ?? w0?.id ?? null,
      dataReady: true,
    }));
  }, []);

  // Boot: validate the cookie via /me. If session is valid, immediately
  // bootstrap the data. If not, mark ready with no user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await api.me();
        if (cancelled) return;
        const su = fromServer(u);
        setState((s) => ({ ...s, user: su, authReady: true }));
        try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(su)); } catch { /* noop */ }
        await loadMarkets();
        try {
          await bootstrapData();
        } catch {
          // Soft-hydrate on failure so per-page fetchers can still work.
          setState((s) => ({ ...s, dataReady: true }));
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          if (cancelled) return;
          setState((s) => ({ ...s, user: null, authReady: true }));
          try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* noop */ }
        } else if (!cancelled) {
          // Network error — surface as unauthenticated so login form shows.
          setState((s) => ({ ...s, user: null, authReady: true }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [bootstrapData, loadMarkets]);

  const value = useMemo<PureHContextValue>(() => {
    const currentCompany   = COMPANIES.find((c) => c.id === state.companyId) ?? COMPANIES[0] ?? null;
    const currentWorkspace = WORKSPACES.find((w) => w.id === state.workspaceId) ?? WORKSPACES[0] ?? null;
    const currentMarket    = state.markets.find((m) => m.id === state.marketId) ?? null;

    return {
      user: state.user,
      companyId: state.companyId,
      workspaceId: state.workspaceId,
      markets: state.markets,
      marketId: state.marketId,
      currentMarket,
      activeCurrency: currentMarket?.currency ?? null,
      authReady: state.authReady,
      dataReady: state.dataReady,
      ready: state.authReady && (state.user ? state.dataReady : true),
      companies: COMPANIES.slice(),
      workspaces: WORKSPACES.filter((w) => !state.companyId || w.companyId === state.companyId),
      currentCompany,
      currentWorkspace,
      login: async (email: string, password: string) => {
        const u = await api.login(email, password);
        const su = fromServer(u);
        setState((s) => ({ ...s, user: su, dataReady: false }));
        try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(su)); } catch { /* noop */ }
        await loadMarkets();
        try { await bootstrapData(); } catch {
          // Soft-hydrate: allow the shell to render even when the global
          // bootstrap fails (e.g. an unrelated legacy collection throws).
          // Pages that fetch their own data (Company Setup, Corporate
          // Identity) still work.
          setState((s) => ({ ...s, dataReady: true }));
        }
      },
      logout: async () => {
        try { await api.logout(); } catch { /* still clear locally */ }
        hydrateAll({});
        setActiveMarketHeader(null);
        setState((s) => ({ ...s, user: null, dataReady: false }));
        try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* noop */ }
      },
      setCompany: (id: string) => {
        const firstWs = WORKSPACES.find((w) => w.companyId === id);
        setState((s) => ({ ...s, companyId: id, workspaceId: firstWs?.id ?? null }));
      },
      setWorkspace: (id: string) => setState((s) => ({ ...s, workspaceId: id })),
      setMarket: (id: string) => {
        setActiveMarketHeader(id);
        try { localStorage.setItem(MARKET_KEY, id); } catch { /* noop */ }
        setState((s) => ({ ...s, marketId: id, dataReady: false }));
        // Re-run the full bootstrap so every hydrated collection re-filters
        // to the new market scope.
        bootstrapData().catch(() => setState((s) => ({ ...s, dataReady: true })));
      },
    };
  }, [state, bootstrapData, loadMarkets]);

  return <PureHContext.Provider value={value}>{children}</PureHContext.Provider>;
}

export function usePureH(): PureHContextValue {
  const ctx = useContext(PureHContext);
  if (!ctx) throw new Error("usePureH must be used inside PureHProvider");
  return ctx;
}
